import { HttpErrorResponse } from '@angular/common/http';
import {
	afterNextRender,
	ChangeDetectorRef,
	Component,
	DestroyRef,
	EventEmitter,
	inject,
	Injector,
	Input,
	OnInit,
	Output,
	runInInjectionContext,
	signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
	AbstractControl,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
	ValidationErrors,
	Validators,
} from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	FormGroupComponent,
	InputComponent,
	ITab,
	ModalComponent,
	SelectComponent,
	SwitchComponent,
	TabsComponent,
	TextEditorComponent,
} from '@tecalisdev/infinity';
import { LoadingService } from '../../../../../shared/services/loading.service';
import { MasterService } from '../../../../../shared/services/master.service';
import { ReactiveFormsService } from '../../../../../shared/services/reactive-forms.service';
import { SnackbarService } from '../../../../../shared/services/snackbar.service';
import {
	ILegalTermsFormPayload,
	ILegalTermsHtmlByLanguage,
	ILegalTermsListBody,
} from '../../models/legal-terms.models';
import { LegalTermsService } from '../../services/legal-terms.service';
import { LanguageService } from '../../../../../core/services/language.service';

/** Validación estricta del contenido HTML del text editor (objeto `{ html: string }`). */
function legalTermsEditorRequired(control: AbstractControl): ValidationErrors | null {
	const v = control.value;
	const html = typeof v === 'string' ? v : v?.html;
	return html && String(html).trim().length > 0 ? null : { required: true };
}

@Component({
	selector: 'app-legal-terms-form',
	imports: [
		ReactiveFormsModule,
		TranslatePipe,
		ModalComponent,
		AlertComponent,
		FormGroupComponent,
		InputComponent,
		TextEditorComponent,
		SwitchComponent,
		SelectComponent,
		TabsComponent,
	],
	templateUrl: './legal-terms-form.component.html',
	styleUrl: './legal-terms-form.component.scss',
})
export class LegalTermsFormComponent implements OnInit {
	private loading = inject(LoadingService);
	private trans = inject(TranslateService);
	private snackbarService = inject(SnackbarService);
	private legalTermsService = inject(LegalTermsService);
	private destroyRef = inject(DestroyRef);
	private languageService = inject(LanguageService);
	private injector = inject(Injector);
	private cdr = inject(ChangeDetectorRef);
	public reactiveFormService = inject(ReactiveFormsService);
	public masterService = inject(MasterService);

	@Input() detail?: ILegalTermsListBody;
	@Input() company?: string;
	@Output() closeEvent = new EventEmitter<boolean>();

	public errorMsg: string = '';
	public readonly allowedLanguages = this.languageService.getAllowedLanguages();
	public formLangTabs: ITab[] = [];
	public selectedFormLang = signal<string>(this.languageService.getLanguage());

	public form: FormGroup = new FormGroup({
		custom_id: new FormControl('', Validators.required),
		required: new FormControl(true, Validators.required),
		html: this.buildHtmlLanguageFormGroup(),
		company_uuid: new FormControl('', Validators.required),
	});

	ngOnInit(): void {
		this.refreshFormLangTabs();
		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.refreshFormLangTabs();
		});

		if (this.masterService.companies.length === 1) {
			this.form.get('company_uuid').setValue(this.masterService.companies[0]);
		}

		if (this.detail) {
			this.patchDetailIntoForm();
		}

		if (this.company) {
			this.form.get('company_uuid').setValue(this.company);
			// Deshabilitar el campo si viene por propiedad
			this.form.get('company_uuid').disable();
		}
	}

	private buildHtmlLanguageFormGroup(): FormGroup {
		const group: Record<string, FormControl> = {};
		for (const lang of this.allowedLanguages) {
			group[lang] = new FormControl({ html: '' }, [legalTermsEditorRequired]);
		}
		return new FormGroup(group);
	}

	private patchDetailIntoForm(): void {
		if (!this.detail) return;

		for (const lang of this.allowedLanguages) {
			const html = this.detail.html[lang] ?? '';
			this.form.get(`html.${lang}`)?.setValue({ html });
		}

		this.form.patchValue({
			custom_id: this.detail.custom_id,
			required: this.detail.required ?? true,
			company_uuid: this.detail.company_uuid,
		});
	}

	private refreshFormLangTabs(): void {
		const current = this.selectedFormLang();
		this.formLangTabs = this.languageService.getAllowedLanguages().map((lang) => ({
			id: lang,
			label: this.trans.instant(`LEGAL_TERMS.LANG.${lang.toUpperCase()}`),
			selected: lang === current,
		}));
	}

	public onFormLangTab(tab: { id: string }): void {
		const id = tab.id;
		this.selectedFormLang.set(id);
		this.formLangTabs = this.formLangTabs.map((t) => ({ ...t, selected: t.id === id }));
	}

	public htmlControlForLang(lang: string): FormControl {
		return this.form.get(['html', lang]) as FormControl;
	}

	public getHtmlPlaceholder(): string {
		const lang = this.selectedFormLang().toUpperCase();
		return `${this.trans.instant('LEGAL_TERMS.FORM.HTML_PLACEHOLDER')} (${lang})`;
	}

	public closeModal(type: string): void {
		const actions = {
			buttonPrimary: () => {
				if (this.form.invalid) {
					this.form.markAllAsTouched();
					this.focusFirstInvalidHtmlLocale();
					return;
				}
				this.detail ? this.updateLegalTerms() : this.createLegalTerms();
			},
		};

		actions[type] ? actions[type]() : this.closeEvent.emit(false);
	}

	/** Pestaña, scroll y foco al primer HTML por idioma inválido (misma idea que el workflow). */
	private focusFirstInvalidHtmlLocale(): void {
		for (const lang of this.allowedLanguages) {
			const c = this.form.get(['html', lang]);
			if (c?.invalid) {
				this.onFormLangTab({ id: lang });
				this.cdr.detectChanges();
				const anchorId = 'legal-terms-html-locale-anchor';
				runInInjectionContext(this.injector, () => {
					afterNextRender(() => {
						document.getElementById(anchorId)?.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
							inline: 'nearest',
						});
						const host = document.getElementById(anchorId);
						const focusable = host?.querySelector(
							'input, textarea, [contenteditable="true"]',
						) as HTMLElement | null;
						focusable?.focus?.();
					});
				});
				return;
			}
		}
	}

	private updateLegalTerms(): void {
		this.loading.show(this.trans.instant('LOADING.UPDATE_LEGAL_TERMS'));
		this.legalTermsService.update(this.detail.uuid, this.getPayload()).subscribe({
			next: (response: any) => {
				this.closeEvent.emit(true);
				this.snackbarService.show(this.trans.instant('LEGAL_TERMS.LIST.UPDATE_CONFIRM'));
			},
			error: (error: HttpErrorResponse) => {
				this.errorMsg = this.trans.instant('LEGAL_TERMS.LIST.UPDATE_ERROR');
				this.loading.hide();
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	private createLegalTerms(): void {
		this.loading.show(this.trans.instant('LOADING.CREATE_LEGAL_TERMS'));
		this.legalTermsService.create(this.getPayload()).subscribe({
			next: (response: any) => {
				this.closeEvent.emit(true);
				this.snackbarService.show(this.trans.instant('LEGAL_TERMS.LIST.CREATE_CONFIRM'));
			},
			error: (error: HttpErrorResponse) => {
				this.errorMsg = this.trans.instant('LEGAL_TERMS.LIST.CREATE_ERROR');
				this.loading.hide();
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	private getPayload(): ILegalTermsFormPayload {
		const html = {} as ILegalTermsHtmlByLanguage;
		for (const lang of this.allowedLanguages) {
			const raw = this.form.get(`html.${lang}`)?.value;
			const htmlStr = typeof raw === 'string' ? raw : (raw?.html ?? '');
			html[lang] = htmlStr;
		}

		return {
			html,
			required: this.form.get('required')?.value,
			custom_id: this.form.get('custom_id')?.value,
			company_uuid: this.form.get('company_uuid').value?.id ?? this.form.get('company_uuid').value,
		};
	}
}
